import { useState, type ChangeEvent } from "react";
import type { GridCellParams } from "@mui/x-data-grid";
import type { Entity, ID, Mapped, ResourceResponse } from "types";
import { compareIds, when } from "util/support";
import { convertToBooleanSwitch } from "util/transformers";
import useGeneralActions from "features/general/useActions";
import { NotificationSeverity } from "features/general/types";

type UseSimpleSwitchArg<T extends unknown> = {
    handleChangeRequest: (
        entity: Mapped<number | string>,
        checked?: boolean
    ) => Promise<ResourceResponse<T>>;
    getRowId?: (row: Entity) => ID;
};

export const useSimpleSwitch = <T extends unknown>({
    handleChangeRequest,
    getRowId = (row: Entity) => row.pki
}: UseSimpleSwitchArg<T>) => {
    const [loadingColumns, setLoadingColumns] = useState<Array<[ID, string]>>([]);

    const { showNotication } = useGeneralActions();

    const onChange = (gridCellParams: GridCellParams, _: ChangeEvent, checked: boolean) => {
        const { row, field } = gridCellParams;

        setLoadingColumns(state => [
            ...state,
            [getRowId(row), field]
        ]);

        let notificationPayload = {
            message: 'Something went wrong!',
            severity: NotificationSeverity.Error
        };

        return handleChangeRequest({
            pki: getRowId(row),
            [field]: convertToBooleanSwitch(checked)
        }, checked)
            .then(response => {
                const {
                    success = false,
                    message = notificationPayload.message
                } = (response ?? {})  as ResourceResponse<T>;

                notificationPayload = {
                    message,
                    severity: when(
                        success,
                        NotificationSeverity.Success,
                        NotificationSeverity.Error
                    )
                };
            })
            .finally(() => {
                showNotication(notificationPayload);
                setLoadingColumns(state =>
                    state.filter(([pki, rowField]) => !(
                        compareIds(pki, getRowId(row)) &&
                        Object.is(rowField, field)
                    ))
                );
            });
    };

    const getCellProps = ({ row, field }: GridCellParams) => (
        when(
            loadingColumns.some(([pki, rowField]) => (
                compareIds(pki, getRowId(row)) &&
                Object.is(rowField, field)
            )),
            {
                loading: true,
                disabled: true
            },
            {
                loading: false,
                disabled: false
            }
        )
    );

    return {
        loadingColumns,
        onChange,
        getCellProps
    };
};

export default useSimpleSwitch;
